import type { InventoryVehicle } from '@carvana/merch-contracts';
import RequestedFeature from '@carvana/merch-contracts/RequestedFeature';
import {
  useEffectWithController,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { isFailure, isSuccess } from '@carvana/merch-result';
import { useContext } from 'react';
import SortOption from '~/constants/common/SortOption';
import DeviceContext, { useIsMobile } from '~/context/common/DeviceContext';
import StaticContext from '~/context/common/StaticContext';
import { useIsLoggedIn, useUserLocation } from '~/context/common/UserContext';
import fetchAnonymousRecentVehicles from '~/services/common/fetchAnonymousRecentVehicles';
import fetchAuthenticatedRecentVehicles from '~/services/common/fetchAuthenticatedRecentVehicles';
import fetchInventory from '~/services/common/fetchInventory';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

const MAX_RECENT_VEHICLE_IDS = 25;
const MAX_HYDRATED_VEHICLES = 12;

type ReturnType = {
  hasError: boolean;
  isLoaded: boolean;
  vehicles: Array<InventoryVehicle>;
};

export default function useGetSrpRecentlyViewed(): ReturnType {
  const { browserName } = useContext(DeviceContext);
  const { clientId, eventCorrelationId } = useContext(StaticContext);
  const isLoggedIn = useIsLoggedIn();
  const isMobile = useIsMobile();
  const { zip } = useUserLocation();
  const {
    data: vehicles,
    hasError,
    isLoaded,
    setData,
    setError,
    setLoaded,
    setLoading
  } = useLoadableState<Array<InventoryVehicle>>([]);

  useEffectWithController(
    controller => {
      const browserCookieId = BrowserCookies.get('BrowserCookieId');

      if (!browserCookieId) {
        setLoaded();

        return;
      }

      setLoading();

      const fetchRecentVehicles = isLoggedIn
        ? fetchAuthenticatedRecentVehicles
        : fetchAnonymousRecentVehicles;

      fetchRecentVehicles(
        {
          browserCookieId,
          correlationId: eventCorrelationId,
          count: MAX_RECENT_VEHICLE_IDS
        },
        controller
      )
        .then(result => {
          if (isFailure(result)) {
            setError(new Error(result.errorMessage));

            return undefined;
          }

          const recentVehicleIds = isSuccess(result)
            ? result.content.recentVehicles.map(({ vehicleId }) => vehicleId)
            : [];

          if (recentVehicleIds.length === 0) {
            setData([]);

            return undefined;
          }

          return fetchInventory(
            {
              analyticsData: {
                browser: browserName,
                clientId,
                deviceName: '',
                isMobileDevice: isMobile,
                referrer: document.referrer
              },
              browserCookieId,
              filters: { vehicleIds: recentVehicleIds },
              pagination: { page: 1, pageSize: MAX_HYDRATED_VEHICLES },
              requestedFeatures: [
                RequestedFeature.ExcludeFacetData,
                RequestedFeature.LoanTermPricing
              ],
              sortBy: SortOption.MostPopular,
              zip5: zip
            },
            controller
          ).then(({ data: inventoryData }) => {
            const orderedVehicles = inventoryData.inventory.vehicles.sort(
              (a, b) =>
                recentVehicleIds.indexOf(a.vehicleId) -
                recentVehicleIds.indexOf(b.vehicleId)
            );

            setData(orderedVehicles);
          });
        })
        .catch(
          generateErrorLogger(
            'Recently Viewed (SRP)',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      browserName,
      clientId,
      eventCorrelationId,
      isLoggedIn,
      isMobile,
      setData,
      setError,
      setLoaded,
      setLoading,
      zip
    ]
  );

  return { hasError, isLoaded, vehicles };
}
